import type { PlannedSet } from './schemas/plan.js';
import { formatWeight } from './units.js';
import type { UnitsPreference } from './schemas/profile.js';

// Pure formatting for the read-only Plan preview surface (PlanPreview).
// No React, no i18n, no catalog lookups — those live in the components.
// Kept pure so the label + "N × reps @ load" collapse rules are
// jest-locked (previewFormat.test.ts) before the presentational layer
// renders them. (The program preview's per-week matrix was replaced by
// the intensity grid — see programIntensity.ts.)

const formatNum = (n: number): string => String(Math.round(n * 100) / 100);

// Label for a resolved Plan set's target. A plan set can carry both a
// weight target and an RPE target (the program resolver keeps RPE on
// targetRpe and leaves the weight empty, but a hand-authored plan may
// set both), so the two join with a middot.
//
// The absolute weight goes through formatWeight, the same formatter the
// session summary and the share card use: it converts out of the stored kg
// and appends the matching unit. It used to print a hand-rolled "N kg", so
// an lb user read a kilo figure labelled kg on a surface where every other
// weight had already been converted. Default "kg" keeps existing callers
// byte-identical.
export const plannedTargetLabel = (
  set: PlannedSet,
  unit: UnitsPreference = 'kg',
  locale = 'en-US',
): string => {
  const parts: string[] = [];
  const w = set.targetWeight;
  // maxFractionDigits 2 preserves the old formatNum rounding for kg; lb
  // keeps formatWeight's 1-decimal plate granularity.
  if (w.kind === 'absolute')
    parts.push(formatWeight(w.weightKg, unit, locale, unit === 'kg' ? 2 : 1));
  else if (w.kind === 'percentage') parts.push(`${formatNum(w.pctOfMax)}%`);
  if (typeof set.targetRpe === 'number') parts.push(`RPE ${formatNum(set.targetRpe)}`);
  if (typeof set.targetRir === 'number') parts.push(`RIR ${formatNum(set.targetRir)}`);
  return parts.join(' · ');
};

type SetCell = { reps: number; label: string; amrap: boolean };

// Compact descriptor for one set: "8 @ 70%", AMRAP floor → "8+ @ 70%",
// AMRAP with no floor → "AMRAP", bodyweight/freestyle (no label) → "8".
const cellText = ({ reps, label, amrap }: SetCell): string => {
  const repsText = amrap ? (reps > 0 ? `${reps}+` : 'AMRAP') : `${reps}`;
  return label ? `${repsText} @ ${label}` : repsText;
};

const sameCell = (a: SetCell, b: SetCell): boolean =>
  a.reps === b.reps && a.label === b.label && a.amrap === b.amrap;

// Collapse a plan entry's set list into one line. Identical sets fold to
// "N × 8 @ 70%"; a single set drops the count; mixed sets list out
// individually. The collapse keeps the line readable on a narrow screen
// instead of stacking three identical rows.
export const formatSetGroup = (cells: SetCell[]): string => {
  if (cells.length === 0) return '';
  const first = cells[0]!;
  const uniform = cells.every((c) => sameCell(c, first));
  if (uniform) {
    return cells.length === 1 ? cellText(first) : `${cells.length} × ${cellText(first)}`;
  }
  return cells.map(cellText).join(' · ');
};
